import React from "react";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { getCalendar } from "Jarvis/JarvisServices/JarvisLocalStorageService";
import jMoment from "moment-jalaali";
import iMoment from "moment-hijri";
import "moment/locale/ar-sa";
import JarvisBaseSettingsService from "Jarvis/JarvisServices/JarvisBaseSettingsService";

export const isNullOrWhiteSpace = (input) => {
  if (input === null || input === undefined) return true;
  if (typeof input === "string") if (input.trim() === "") return true;
  return false;
};

export const ifNullOrWhiteSpace = (input, replacement) => {
  if (isNullOrWhiteSpace(input)) return replacement;
  return input;
};

export const isTrue = (input) => {
  input = input?.toString();
  if (input?.toLowerCase() === "true" || input === "1") return true;
  return false;
};

export const uuid = () => {
  return uuidv4();
};

/*Capitals and digits only: codes go into fields validated as type="code"
  (/^[A-Za-z0-9._-]+$/), and a code someone may have to read out or retype is better off without
  characters that look alike in print.*/
const CODE_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

/**
 * A random code of `length` characters, drawn from the browser's CSPRNG.
 *
 * Used where a form needs a code up front without asking the server for the next one in sequence.
 * Uniqueness is guaranteed by the unique index on the column, not here - but at 36 characters over
 * 16 places the chance of ever drawing the same code twice is not worth planning for.
 */
export const generateCode = (length = 16) => {
  const size = CODE_ALPHABET.length;
  // 2^32 is not a multiple of the alphabet, so a plain modulo would make its first few characters
  // likelier than the rest. The values in the uneven tail are redrawn instead of folded in.
  const limit = Math.floor(0x100000000 / size) * size;
  const buffer = new Uint32Array(length);
  window.crypto.getRandomValues(buffer);

  let code = "";
  const redraw = new Uint32Array(1);
  for (let i = 0; i < length; i++) {
    let value = buffer[i];
    while (value >= limit) {
      window.crypto.getRandomValues(redraw);
      value = redraw[0];
    }
    code += CODE_ALPHABET[value % size];
  }

  return code;
};

export const isEqual = (string1, string2) => {
  if (string1?.toUpperCase() === string2?.toUpperCase()) return true;
  return false;
};

export const isValidDate = (date, format) => {
  const calendar = getCalendar();

  if (isEqual(calendar.calendarKey, "Gregorian") === false)
    format = "yyyy/MM/dd";

  format = ifNullOrWhiteSpace(
    format,
    ifNullOrWhiteSpace(calendar?.calendarFormat, "yyyy/MM/dd")
  );

  const dt = moment(
    date?.replaceAll("/", "-"),
    format?.toUpperCase().replaceAll("/", "-"),
    true
  );
  return dt.isValid();
};

export const getDateString = (date) => {
  if (isNullOrWhiteSpace(date)) date = new Date();
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${date.getFullYear()}/${mm}/${dd}`;
};

export const getTodayDateTime = () => {
  const dateTime = new Date();
  const date = getDateString(dateTime);
  const hh = String(dateTime.getHours()).padStart(2, "0");
  const mm = String(dateTime.getMinutes()).padStart(2, "0");
  return `${convertGregorianTo(date)} ${hh}:${mm}`;
};

export const convertGregorianTo = (date, calendar) => {
  if (isNullOrWhiteSpace(date)) return "";
  calendar = ifNullOrWhiteSpace(calendar, getCalendar().calendarKey);
  if (isEqual(calendar, "Gregorian")) return date;
  else if (isEqual(calendar, "HijriShamsi")) {
    const symbolMap = {
      1: "1",
      2: "2",
      3: "3",
      4: "4",
      5: "5",
      6: "6",
      7: "7",
      8: "8",
      9: "9",
      0: "0",
    };
    jMoment.updateLocale("ar-sa", {
      postformat: function (string) {
        return string
          .replace(/\d/g, function (match) {
            return symbolMap[match];
          })
          .replace(/,/g, "،");
      },
    });
    const _date = jMoment(
      date?.replaceAll("-", "/"),
      "YYYY/MM/DD",
      false
    ).format("jYYYY/jMM/jDD");
    if (_date !== "Invalid date") return _date;
    return "";
  } else if (isEqual(calendar, "HijriGhamari")) {
    const symbolMap = {
      1: "1",
      2: "2",
      3: "3",
      4: "4",
      5: "5",
      6: "6",
      7: "7",
      8: "8",
      9: "9",
      0: "0",
    };
    iMoment.updateLocale("ar-sa", {
      postformat: function (string) {
        return string
          .replace(/\d/g, function (match) {
            return symbolMap[match];
          })
          .replace(/,/g, "،");
      },
    });
    const _date = iMoment(date, "YYYY/MM/DD", true).format("iYYYY/iMM/iDD");
    if (_date !== "Invalid date") return _date;
    return "";
  } else return date;
};

export const convertToGregorian = (date, calendar) => {
  calendar = ifNullOrWhiteSpace(calendar, getCalendar().calendarKey);
  if (isEqual(calendar, "Gregorian")) return date;
  else if (isEqual(calendar, "HijriShamsi")) {
    const dialect = "persian-modern";
    jMoment.loadPersian({
      dialect: dialect,
      usePersianDigits: false, //usePersianDigits,
    });
    jMoment.updateLocale("fa", {
      jMonthsShort: {
        persian:
          "فروردین_اردیبهشت_خرداد_تیر_مرداد_شهریور_مهر_آبان_آذر_دی_بهمن_اسفند".split(
            "_"
          ),
        "persian-modern":
          "فروردین_اردیبهشت_خرداد_تیر_مرداد_شهریور_مهر_آبان_آذر_دی_بهمن_اسفند".split(
            "_"
          ),
      }[dialect],
    });
    const _date = jMoment(date, "jYYYY/jMM/jDD", true).format("YYYY/MM/DD");
    if (_date !== "Invalid date") return _date;
    return "";
  } else if (isEqual(calendar, "HijriGhamari")) {
    const symbolMap = {
      1: "1",
      2: "2",
      3: "3",
      4: "4",
      5: "5",
      6: "6",
      7: "7",
      8: "8",
      9: "9",
      0: "0",
    };
    iMoment.updateLocale("ar-sa", {
      postformat: function (string) {
        return string
          .replace(/\d/g, function (match) {
            return symbolMap[match];
          })
          .replace(/,/g, "،");
      },
    });
    const _date = iMoment(date, "iYYYY/iMM/iDD", true).format("YYYY/MM/DD");
    if (_date !== "Invalid date") return _date;
    return "";
  } else return date;
};
export const generateJarvisErrorHandling = ({
  errorCode,
  errorMessage,
  isSuccessful,
  mustBeSeenByUser,
}) => {
  return {
    ErrorCode: ifNullOrWhiteSpace(errorCode, 0),
    ErrorMessage: ifNullOrWhiteSpace(errorMessage, ""),
    IsSuccessful: ifNullOrWhiteSpace(isSuccessful, true),
    MustBeSeenByUser: ifNullOrWhiteSpace(mustBeSeenByUser, false),
  };
};
export const generateJarvisOutput = ({
  errorCode,
  errorMessage,
  isSuccessful,
  mustBeSeenByUser,
  data,
}) => {
  return {
    ErrorHandling: generateJarvisErrorHandling({
      errorCode,
      errorMessage,
      isSuccessful,
      mustBeSeenByUser,
    }),
    Data: data,
  };
};

export const digitGrouping = (number) =>
  number?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const convertToBoolean = (value) => {
  if (isNullOrWhiteSpace(value)) return false;
  if (_.isBoolean(value)) return value;
  if (_.isString(value) && (value === "1" || value.toUpperCase() === "TRUE")) {
    return true;
  }
  if (_.isNumber(value) && value !== 0) return true;

  return false;
};

export const convertToNumber = (value) => {
  if (_.isString(value)) {
    return Number(value.replaceAll(",", ""));
  }

  return value;
};

export const flatten = (object) => {
  const values = _.values(object);
  if (_.isObject(values[0])) return _.merge(...values);
  if (_.isArray(values[0])) return _.flatten(values);

  return object;
};

export const generateRules = (rules, IGTC) => {
  const result = rules || [];
  let index = -1;
  if (IGTC) {
    flatten(IGTC).forEach((column) => {
      if (rules) {
        index = rules?.findIndex((rule) => rule.ColumnName === column);
        if (index !== -1) result.push({ ...rules[index], Dispaly: false });
        else result.push({ ColumnName: column, Dispaly: false });
      } else result.push({ ColumnName: column, Dispaly: false });
    });
  }

  return _.isArray(result) ? result : null;
};

export const generateColumns = (columns, prerequisites) => {
  let { GTCD, rules } = prerequisites;

  if (!_.isEmpty(GTCD)) {
    const newColumns = _.isArray(GTCD) ? GTCD : flatten(GTCD) || [];
    let column = {};
    newColumns.forEach((col) => {
      column = { visible: false };
      column.label = col.LangDescription;
      column.path = col.ColumnName;
      switch (col.DataType) {
        case "Bit": {
          column.type = "checkbox";
          column.widthScale = 0.5;
          break;
        }
        case "Date":
        case "DateTIme":
          column.type = "date";
          break;

        case "TinyInt":
        case "SmallInt":
        case "Int":
        case "BigInt":
        case "Real":
        case "Decimal":
          column.type = "number";
          break;

        default:
          break;
      }

      columns.push(column);
    });
  }

  if (!_.isEmpty(prerequisites.IGTC))
    prerequisites.IGTC = _.isArray(prerequisites.IGTC)
      ? prerequisites.IGTC
      : flatten(prerequisites.IGTC);

  if (!_.isEmpty(rules))
    rules.forEach((rule) => {
      if (
        rule.Display === false &&
        (!prerequisites.IGTC ||
          prerequisites.IGTC.indexOf(rule.ColumnName) === -1)
      ) {
        if (_.isNil(prerequisites.IGTC)) prerequisites.IGTC = [];
        prerequisites.IGTC.push(rule.ColumnName);
      }
    });

  if (!_.isEmpty(prerequisites.IGTC))
    return columns.filter(
      (column) => prerequisites.IGTC.indexOf(column.path) === -1
    );
  else return columns;
};

export const checkClassName = (className, columnName, prerequisites) => {
  let { IGTC } = prerequisites ?? {};

  if (_.isEmpty(IGTC)) return className;
  if (_.isArray(columnName)) {
    const picked = _.pickBy(
      IGTC,
      (col) => columnName.indexOf(col.ColumnName) !== -1
    );
    if (Object.keys(picked).length === columnName.length) return "d-none";

    return className;
  } else if (IGTC.indexOf(columnName) !== -1) return "d-none";

  return className;
};

export const addGTCD = (source, GTCD, dest) => {
  if (GTCD && !_.isArray(GTCD)) GTCD = flatten(GTCD);
  if (!_.isEmpty(GTCD)) {
    if (dest.GTCD === undefined) dest.GTCD = [];
    let gtcdModel = {};

    GTCD.forEach((col) => {
      if (!isNullOrWhiteSpace(source[col.ColumnName])) {
        gtcdModel.Id = col.Id;
        gtcdModel.VT = col.VT;
        gtcdModel.Value = source[col.ColumnName];
        gtcdModel.GTId = col.GTId;

        dest.GTCD.push(gtcdModel);
      }
    });
  }
};

export const getBase64Async = (filePath) => {
  const temporaryFileReader = new FileReader();
  return new Promise((resolve, reject) => {
    temporaryFileReader.onerror = () => {
      temporaryFileReader.abort();
      reject(new DOMException("Problem parsing input file."));
    };
    temporaryFileReader.onloadend = () => {
      resolve(temporaryFileReader.result);
    };
    temporaryFileReader.readAsDataURL(filePath);
  });
};

export const getBase64 = (filePath, callback) => {
  let reader = new FileReader();
  reader.readAsDataURL(filePath);
  reader.onload = function () {
    callback(reader.result);
  };
  reader.onerror = function (error) {
    console.log("Error: ", error);
  };
};

export const json2Table = ({
  jsonArray,
  tableStyle = null,
  theadClassName = null,
   tdStyle = null,
  thStyle = null,
}) => {
  // get table column
  if (jsonArray === null || jsonArray.length === 0) return null;
  const column = Object.keys(jsonArray[0]);

  // get table heading data
  const ThData = () => {
    return column.map((data) => {
      return <th key={uuidv4()} style={thStyle}>{data}</th>;
    });
  };

  // get table row data
  const tdData = () => {
    return jsonArray.map((data) => {
      return (
        <tr key={uuidv4()}>
          {column.map((v) => {
            return <td key={uuidv4()} style={tdStyle}>{data[v]}</td>;
          })}
        </tr>
      );
    });
  };

  return (
    <table className="table" style={tableStyle}>
      <thead className={theadClassName ? theadClassName : ""}>
        <tr>{ThData()}</tr>
      </thead>
      <tbody>{tdData()}</tbody>
    </table>
  );
};

/**
 * json2Table
 *
 * Renders a scrollable table from a JSON array.
 * - Sticky header (stays visible while body scrolls)
 * - No display:block hacks — columns stay perfectly aligned
 * - Fully customisable via style/className props
 *
 * @param {Object[]} jsonArray       - Array of flat objects to render
 * @param {Object}   tableStyle      - Style applied to <table>
 * @param {string}   theadClassName  - ClassName for <thead> (e.g. "thead-dark")
 * @param {Object}   tdStyle         - Style applied to every <td>
 * @param {Object}   thStyle         - Style applied to every <th>
 * @param {string}   maxHeight       - Max height of the scrollable body (default "400px")
 * @param {Object}   columnConfig    - Optional per-column overrides: { ColumnName: { width, render } }
 */
export const json2Table1 = ({
  jsonArray,
  tableStyle = {},
  theadClassName = "",
  tdStyle = {},
  thStyle = {},
  maxHeight = "400px",
  columnConfig = {},
}) => {
  if (!jsonArray || jsonArray.length === 0) return null;

  const columns = Object.keys(jsonArray[0]);

  return (
    <div
      style={{
        width: "100%",
        overflowX: "auto",          // horizontal scroll if table is wide
      }}
    >
      <div
        style={{
          maxHeight,
          overflowY: "auto",        // vertical scroll on body only
          position: "relative",
        }}
      >
        <table
          className="table"
          style={{
            borderCollapse: "collapse",
            width: "100%",
            tableLayout: "fixed",   // prevents column jump during scroll
            ...tableStyle,
          }}
        >
          <thead
            className={theadClassName}
            style={{
              position: "sticky",   // header stays on top while body scrolls
              top: 0,
              zIndex: 2,
            }}
          >
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  style={{
                    width: columnConfig[col]?.width ?? "auto",
                    padding: "10px 12px",
                    textAlign: "left",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    ...thStyle,
                  }}
                  title={col}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {jsonArray.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                style={{
                  backgroundColor: rowIndex % 2 === 0 ? "#fff" : "#f9f9f9", // zebra striping
                }}
              >
                {columns.map((col) => {
                  const cellValue = row[col];
                  const customRender = columnConfig[col]?.render;

                  return (
                    <td
                      key={`${col}_${rowIndex}`}
                      style={{
                        padding: "8px 12px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 0,        // needed for ellipsis to work with tableLayout:fixed
                        ...tdStyle,
                      }}
                      title={String(cellValue ?? "")}  // tooltip shows full value on hover
                    >
                      {customRender
                        ? customRender(cellValue, row, rowIndex)
                        : (cellValue ?? "—")}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Row count footer */}
      <div
        style={{
          padding: "6px 12px",
          fontSize: 12,
          color: "#888",
          borderTop: "1px solid #e0e0e0",
          textAlign: "right",
        }}
      >
        {jsonArray.length} record{jsonArray.length !== 1 ? "s" : ""}
      </div>
    </div>
  );
};

export const openFileViewer = ({ url, filename }) => {
  const fileExtension = filename.split(".").pop();
  let downloadLink = url;
  switch (fileExtension?.toLowerCase()) {
    case "pdf":
      downloadLink = `${JarvisBaseSettingsService.getJarvisUIXUrl()}?module=fileViewer&val=${encodeURIComponent(
        downloadLink
      )}&fileExtension=${fileExtension}`;
      break;
    default:
      break;
  }
  let alink = document.createElement("a");
  alink.href = downloadLink;
  alink.target = "_blank";
  alink.click();
};

export const copyToClipboard = (text) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

export const copyToClipboardApi = (text) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      console.log("Text copied to clipboard");
    })
    .catch((err) => {
      try {
        copyToClipboard(text);
      } catch (e) {
        console.error("Failed to copy text: ", { err, e });
      }
    });
};

export const getDataType = (value) => {
  switch (_.get(value, "DataTypeCode")) {
    case "TinyInt":
    case "SmallInt":
    case "Int":
    case "BigInt":
    case "Real":
    case "Decimal":
      return "Number";
    case "String64":
    case "String128":
    case "String512":
    case "String1024":
    case "StringMax":
      return "String";
    case "Date":
    case "DateTime":
    case "Time":
      return "Date";
    default:
      return _.get(value, "DataTypeCode");
  }
};

export const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const groupKey = item[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
};

export const normalizeKeysToCamelCase = (data) => {
  if (Array.isArray(data)) {
    return data.map((item) => normalizeKeysToCamelCase(item));
  } else if (_.isPlainObject(data)) {
    return _.mapValues(
      _.mapKeys(data, (value, key) => _.camelCase(key)),
      (value) => normalizeKeysToCamelCase(value)
    );
  }
  return data;
};

export const normalizeKeysToProperCase = (data) => {
  if (Array.isArray(data)) {
    return data.map((item) => normalizeKeysToProperCase(item));
  } else if (_.isPlainObject(data)) {
    return _.mapValues(
      _.mapKeys(data, (value, key) =>
        _.upperFirst(_.camelCase(key))
      ),
      (value) => normalizeKeysToProperCase(value)
    );
  }
  return data;
};

export function deepParse(input) {
  let initialData = input;

  if (typeof input === 'string') {
    try {
      initialData = JSON.parse(input);
    } catch (e) {
      console.error("Failed to parse initial JSON string:", e);
      return null;
    }
  } else if (typeof input !== 'object' || input === null) {
    return input;
  }
  return deepParseData(initialData);
}

function deepParseData(data) {
  if (typeof data !== 'object' || data === null) {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(deepParseData);
  }

  const processedObject = {};
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      let value = data[key];

      if (typeof value === 'string') {
        try {
          const parsedValue = JSON.parse(value);

          processedObject[key] = deepParseData(parsedValue);
        } catch (e) {
          processedObject[key] = value;
        }
      } else {
        processedObject[key] = deepParseData(value);
      }
    }
  }
  return processedObject;
}

export const formatFileSize = (bytes, decimals = 2) => {
  if (!bytes || bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}

export const parseJson = (input) => {
  try {
    if (typeof input === "object" && input !== null) {
      return input;
    }
    if (typeof input === "string") {
      return JSON.parse(input);
    }

    console.error("parseJson: Unsupported input type", typeof input);
    return null;
  } catch (error) {
    console.error("parseJson: Failed to parse JSON", error);
    return null;
  }
};

export const joinNonEmpty = (arr, separator = ", ") => {
  if (!arr || arr.length === 0) return "";

  return arr
    .filter(item => item !== null && item !== undefined && String(item).trim() !== "")
    .join(separator);
};
